import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  MapPin, Users, Wifi, QrCode, LogOut, Plus, Copy, Settings,
  Loader2, CheckCircle, BarChart3, Activity, ShoppingBag,
  Package, Edit, Save, X, Image, Upload, Truck, Clock, XCircle,
  Trophy, Star, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TAPTAG_LOGO = "https://customer-assets.emergentagent.com/job_726f4ba1-c975-4f39-ba02-ad3131a12031/artifacts/hgye1jst_IMG_0895%20%281%29.PNG";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalStickers: 0,
    activeStickers: 0,
    totalScans: 0,
    totalOrders: 0,
    pendingOrders: 0,
    totalRevenue: 0
  });
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [codeCount, setCodeCount] = useState(5);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [uploading, setUploading] = useState(false);
  
  // Product edit state
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    image: '',
    dimensions: '',
    features: '',
    packages: [{ quantity: 1, price: 0, label: '1 copë' }]
  });

  // Reward config state
  const [rewardLevels, setRewardLevels] = useState([]);
  const [pointsPerScan, setPointsPerScan] = useState(10);
  const [savingRewards, setSavingRewards] = useState(false);

  useEffect(() => {
    // Check admin auth
    const isAdmin = localStorage.getItem('taptag_admin');
    if (!isAdmin) {
      navigate('/admin');
      return;
    }
    
    fetchStats();
    fetchGeneratedCodes();
    fetchProducts();
    fetchOrders();
    fetchRewardConfig();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGeneratedCodes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/codes`);
      setGeneratedCodes(response.data || []);
    } catch (error) {
      console.error('Error fetching codes:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/shop/products`);
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/orders`);
      setOrders(response.data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchRewardConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rewards/levels`);
      setRewardLevels(response.data.levels || []);
      setPointsPerScan(response.data.points_per_scan || 10);
    } catch (error) {
      console.error('Error fetching reward config:', error);
    }
  };

  const updateLevelPoints = (level, newPoints) => {
    setRewardLevels(prev => prev.map(l => 
      l.level === level ? { ...l, min_points: Math.max(0, parseInt(newPoints) || 0) } : l
    ));
  };

  const updateLevelName = (level, newName) => {
    setRewardLevels(prev => prev.map(l => 
      l.level === level ? { ...l, name: newName } : l
    ));
  };

  const updateLevelReward = (level, field, value) => {
    setRewardLevels(prev => prev.map(l => {
      if (l.level !== level) return l;
      const reward = l.reward ? { ...l.reward } : { type: 'sticker', quantity: 1, message: '' };
      reward[field] = field === 'quantity' ? (parseInt(value) || 1) : value;
      return { ...l, reward };
    }));
  };

  const toggleLevelReward = (level, hasReward) => {
    setRewardLevels(prev => prev.map(l => {
      if (l.level !== level) return l;
      if (hasReward) {
        return { ...l, reward: { type: 'sticker', quantity: 1, message: '' } };
      }
      return { ...l, reward: null };
    }));
  };

  const saveRewardConfig = async () => {
    setSavingRewards(true);
    try {
      await axios.put(`${API_URL}/api/admin/rewards/levels`, {
        levels: rewardLevels.map(l => ({
          level: l.level,
          name: l.name,
          min_points: l.min_points,
          reward: l.reward ? {
            type: l.reward.type,
            quantity: l.reward.quantity,
            message: l.reward.message
          } : null
        })),
        points_per_scan: pointsPerScan
      });
      toast.success('Konfigurimi i pikëve u ruajt!');
    } catch (error) {
      console.error('Error saving reward config:', error);
      toast.error('Gabim në ruajtjen e konfigurimit');
    } finally {
      setSavingRewards(false);
    }
  };

  const generateCodes = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/generate-codes`, {
        count: codeCount
      });
      setGeneratedCodes(prev => [...response.data.codes, ...prev]);
      toast.success(`${codeCount} kode u gjeneruan me sukses!`);
    } catch (error) {
      console.error('Error generating codes:', error);
      toast.error('Gabim në gjenerimin e kodeve');
    } finally {
      setGenerating(false);
    }
  };

  const copyCode = (code) => {
    const scanUrl = `${window.location.origin}/scan/${code}`;
    navigator.clipboard.writeText(scanUrl);
    toast.success('URL e stikerit u kopjua!');
  };

  const deleteCode = async (code) => {
    if (!window.confirm('Je i sigurt që dëshiron të fshish këtë kod?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/codes/${code}`);
      setGeneratedCodes(generatedCodes.filter(c => c.code !== code));
      toast.success('Kodi u fshi!');
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Gabim në fshirjen e kodit');
      }
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Je i sigurt që dëshiron të fshish këtë porosi?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/orders/${orderId}`);
      setOrders(orders.filter(o => o.id !== orderId));
      toast.success('Porosia u fshi!');
      fetchStats();
    } catch (error) {
      toast.error('Gabim në fshirjen e porosisë');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('taptag_admin');
    navigate('/');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setProductForm(prev => ({
        ...prev,
        image: `${API_URL}${response.data.url}`
      }));
      toast.success('Foto u ngarkua me sukses!');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Gabim në ngarkimin e fotos');
    } finally {
      setUploading(false);
    }
  };

  const startEditProduct = (product) => {
    setEditingProduct(product.id);
    setProductForm({
      name: product.name,
      description: product.description,
      image: product.image,
      dimensions: product.dimensions,
      features: product.features.join(', '),
      packages: product.packages
    });
  };

  const cancelEditProduct = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      image: '',
      dimensions: '',
      features: '',
      packages: [{ quantity: 1, price: 0, label: '1 copë' }]
    });
  };

  const saveProduct = async () => {
    try {
      const productData = {
        name: productForm.name,
        description: productForm.description,
        image: productForm.image,
        dimensions: productForm.dimensions,
        features: productForm.features.split(',').map(f => f.trim()).filter(f => f),
        packages: productForm.packages
      };

      await axios.put(`${API_URL}/api/admin/products/${editingProduct}`, productData);
      toast.success('Produkti u përditësua!');
      fetchProducts();
      cancelEditProduct();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Gabim në ruajtjen e produktit');
    }
  };

  const updatePackagePrice = (index, field, value) => {
    const newPackages = [...productForm.packages];
    newPackages[index] = { ...newPackages[index], [field]: field === 'price' || field === 'quantity' ? Number(value) : value };
    setProductForm({ ...productForm, packages: newPackages });
  };

  const addPackage = () => {
    setProductForm({
      ...productForm,
      packages: [...productForm.packages, { quantity: 1, price: 0, label: '' }]
    });
  };

  const removePackage = (index) => {
    const newPackages = productForm.packages.filter((_, i) => i !== index);
    setProductForm({ ...productForm, packages: newPackages });
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/orders/${orderId}/status`, { status: newStatus });
      setOrders(orders.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
      toast.success('Statusi u përditësua!');
      fetchStats();
    } catch (error) {
      toast.error('Gabim në përditësimin e statusit');
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700 border-0"><Clock className="w-3 h-3 mr-1" />Në Pritje</Badge>;
      case 'delivered':
        return <Badge className="bg-emerald-100 text-emerald-700 border-0"><CheckCircle className="w-3 h-3 mr-1" />E Dorëzuar</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700 border-0"><XCircle className="w-3 h-3 mr-1" />E Anuluar</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src={TAPTAG_LOGO}
                alt="TAPTAG Logo"
                className="w-10 h-10 rounded-full object-cover shadow"
              />
              <div>
                <span className="text-xl font-bold text-slate-900">Admin Panel</span>
                <p className="text-xs text-slate-500">TAPTAG Management</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" className="text-slate-600 hover:text-slate-900">
                  <MapPin className="w-4 h-4 mr-2" />
                  Faqja Kryesore
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                className="text-slate-600 hover:text-slate-900"
                onClick={handleLogout}
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Dil
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'overview', label: 'Përmbledhje', icon: BarChart3 },
              { id: 'codes', label: 'Kodet NFC', icon: QrCode },
              { id: 'products', label: 'Produktet', icon: Package },
              { id: 'orders', label: 'Porositë', icon: ShoppingBag },
              { id: 'rewards', label: 'Pikët', icon: Trophy }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-violet-600 border-b-2 border-violet-500'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="border-0 shadow-lg bg-gradient-to-br from-violet-500 to-purple-600 text-white" data-testid="stat-users">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                      <Users className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-sm text-white/80">Përdorues</p>
                      <p className="text-3xl font-bold">{stats.totalUsers}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg bg-gradient-to-br from-cyan-500 to-teal-600 text-white" data-testid="stat-stickers">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                      <Wifi className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-sm text-white/80">Objekte</p>
                      <p className="text-3xl font-bold">{stats.totalStickers}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-500 to-green-600 text-white" data-testid="stat-orders">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                      <ShoppingBag className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-sm text-white/80">Porosi</p>
                      <p className="text-3xl font-bold">{stats.totalOrders}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white" data-testid="stat-revenue">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                      <BarChart3 className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-sm text-white/80">Të Ardhura</p>
                      <p className="text-3xl font-bold">{stats.totalRevenue} LEK</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="border border-slate-200">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Aktivë</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.activeStickers}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-slate-200">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                    <QrCode className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Skanime</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.totalScans}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-slate-200">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Në Pritje</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.pendingOrders}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Codes Tab */}
        {activeTab === 'codes' && (
          <Card className="border border-slate-200" data-testid="code-generator">
            <CardHeader className="border-b border-slate-200">
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <QrCode className="w-5 h-5 text-violet-600" />
                Gjenerator i Kodeve NFC
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <Label className="text-sm text-slate-600 mb-2 block">Numri i kodeve</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={codeCount}
                    onChange={(e) => setCodeCount(parseInt(e.target.value) || 1)}
                    className="border-slate-300"
                    data-testid="code-count-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={generateCodes}
                    disabled={generating}
                    className="bg-gradient-to-r from-violet-600 to-purple-600 text-white h-10 px-6"
                    data-testid="generate-codes-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Duke gjeneruar...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Gjenero Kode
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <p className="text-sm text-slate-500 mb-4">Kodet janë 6 karaktere (p.sh. ABC123)</p>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {generatedCodes.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">
                    Asnjë kod i gjeneruar. Kliko "Gjenero Kode" për të krijuar kode të reja.
                  </p>
                ) : (
                  generatedCodes.map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                      data-testid={`code-item-${index}`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${item.is_used ? 'bg-emerald-100' : 'bg-violet-100'}`}>
                          {item.is_used ? (
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <Wifi className="w-5 h-5 text-violet-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-mono text-lg font-bold text-slate-900 tracking-wider">{item.code}</p>
                            <Badge className={item.is_used ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'}>
                              {item.is_used ? 'I përdorur' : 'I disponueshëm'}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500 mt-1 truncate">
                            {window.location.origin}/scan/{item.code}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyCode(item.code)}
                          className="text-violet-600 border-violet-200 hover:bg-violet-50"
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Kopjo URL
                        </Button>
                        {!item.is_used && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteCode(item.code)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Menaxho Produktet</h2>
            </div>

            <div className="grid gap-6">
              {products.map((product) => (
                <Card key={product.id} className="border border-slate-200">
                  <CardContent className="p-6">
                    {editingProduct === product.id ? (
                      /* Edit Form */
                      <div className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-slate-600">Emri i Produktit</Label>
                            <Input
                              value={productForm.name}
                              onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                              className="mt-1 border-slate-300"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-600">Përmasat</Label>
                            <Input
                              value={productForm.dimensions}
                              onChange={(e) => setProductForm({...productForm, dimensions: e.target.value})}
                              className="mt-1 border-slate-300"
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-slate-600">Përshkrimi</Label>
                          <Textarea
                            value={productForm.description}
                            onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                            className="mt-1 border-slate-300"
                          />
                        </div>

                        <div>
                          <Label className="text-slate-600">Foto e Produktit</Label>
                          <div className="mt-2">
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileUpload}
                              accept="image/*"
                              className="hidden"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploading}
                              className="w-full h-24 border-2 border-dashed border-violet-300 text-violet-600 hover:bg-violet-50 hover:border-violet-400 flex flex-col items-center justify-center gap-2"
                            >
                              {uploading ? (
                                <Loader2 className="w-6 h-6 animate-spin" />
                              ) : (
                                <>
                                  <Upload className="w-6 h-6" />
                                  <span>Kliko për të ngarkuar foton</span>
                                </>
                              )}
                            </Button>
                          </div>
                          {productForm.image && (
                            <div className="mt-3 flex items-center gap-3">
                              <img src={productForm.image} alt="Preview" className="w-20 h-20 object-cover rounded-lg border" />
                              <span className="text-sm text-slate-500 truncate flex-1">{productForm.image.split('/').pop()}</span>
                            </div>
                          )}
                        </div>

                        <div>
                          <Label className="text-slate-600">Veçoritë (ndarë me presje)</Label>
                          <Input
                            value={productForm.features}
                            onChange={(e) => setProductForm({...productForm, features: e.target.value})}
                            className="mt-1 border-slate-300"
                            placeholder="Rezistent ndaj ujit, I qëndrueshëm, ..."
                          />
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-slate-600">Paketat dhe Çmimet</Label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={addPackage}
                              className="text-violet-600"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Shto Paketë
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {productForm.packages.map((pkg, idx) => (
                              <div key={idx} className="flex gap-2 items-center">
                                <Input
                                  type="number"
                                  value={pkg.quantity}
                                  onChange={(e) => updatePackagePrice(idx, 'quantity', e.target.value)}
                                  className="w-20 border-slate-300"
                                  placeholder="Sasia"
                                />
                                <Input
                                  value={pkg.label}
                                  onChange={(e) => updatePackagePrice(idx, 'label', e.target.value)}
                                  className="flex-1 border-slate-300"
                                  placeholder="Label (p.sh. 5 copë)"
                                />
                                <Input
                                  type="number"
                                  value={pkg.price}
                                  onChange={(e) => updatePackagePrice(idx, 'price', e.target.value)}
                                  className="w-24 border-slate-300"
                                  placeholder="Çmimi"
                                />
                                <span className="text-slate-500 text-sm">LEK</span>
                                {productForm.packages.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removePackage(idx)}
                                    className="text-red-500 hover:text-red-600"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="flex gap-2 pt-4">
                          <Button onClick={saveProduct} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                            <Save className="w-4 h-4 mr-2" />
                            Ruaj
                          </Button>
                          <Button variant="outline" onClick={cancelEditProduct}>
                            <X className="w-4 h-4 mr-2" />
                            Anulo
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* Display Mode */
                      <div className="flex gap-6">
                        <div className="w-32 h-32 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-lg font-bold text-slate-900">{product.name}</h3>
                              <p className="text-slate-600 text-sm mt-1">{product.description}</p>
                              <p className="text-slate-500 text-xs mt-2">Përmasat: {product.dimensions}</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => startEditProduct(product)}
                              className="text-violet-600 border-violet-200 hover:bg-violet-50"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edito
                            </Button>
                          </div>
                          <div className="mt-4">
                            <p className="text-slate-600 text-sm mb-2">Çmimet:</p>
                            <div className="flex flex-wrap gap-2">
                              {product.packages.map((pkg, idx) => (
                                <span key={idx} className="bg-violet-100 text-violet-700 px-3 py-1 rounded-full text-sm font-medium">
                                  {pkg.label}: {pkg.price} LEK
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Porositë</h2>
              <div className="flex gap-2">
                <Badge className="bg-amber-100 text-amber-700">Në Pritje: {orders.filter(o => o.status === 'pending').length}</Badge>
                <Badge className="bg-emerald-100 text-emerald-700">Dorëzuar: {orders.filter(o => o.status === 'delivered').length}</Badge>
              </div>
            </div>

            {orders.length === 0 ? (
              <Card className="border border-slate-200">
                <CardContent className="p-12 text-center">
                  <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Asnjë porosi ende</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <Card key={order.id} className="border border-slate-200">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="font-bold text-slate-900">
                              {order.customer_name} {order.customer_surname}
                            </h3>
                            {getStatusBadge(order.status)}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Adresa</p>
                              <p className="text-slate-900">{order.customer_address}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Telefoni</p>
                              <p className="text-slate-900">{order.customer_phone}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Email</p>
                              <p className="text-slate-900">{order.customer_email}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Pagesa</p>
                              <p className="text-slate-900">{order.payment_method === 'cash' ? 'Cash në dorëzim' : 'Kartë'}</p>
                            </div>
                          </div>

                          <div className="mt-4 pt-4 border-t border-slate-200">
                            <p className="text-sm text-slate-500 mb-2">Produktet:</p>
                            {order.items.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm">
                                <span>{item.product_name} - {item.package_label}</span>
                                <span className="font-semibold">{item.price} LEK</span>
                              </div>
                            ))}
                            <div className="flex items-center justify-between mt-2 pt-2 border-t font-bold">
                              <span>Total</span>
                              <span className="text-violet-600">{order.total_amount} LEK</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          <Button
                            size="sm"
                            onClick={() => updateOrderStatus(order.id, 'delivered')}
                            disabled={order.status === 'delivered'}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <Truck className="w-4 h-4 mr-1" />
                            Dorëzuar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateOrderStatus(order.id, 'pending')}
                            disabled={order.status === 'pending'}
                          >
                            <Clock className="w-4 h-4 mr-1" />
                            Në Pritje
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateOrderStatus(order.id, 'cancelled')}
                            disabled={order.status === 'cancelled'}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Anulo
                          </Button>
                          <hr className="my-1 border-slate-200" />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteOrder(order.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Fshi
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Rewards/Points Tab */}
        {activeTab === 'rewards' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Konfigurimi i Pikëve dhe Niveleve</h2>
            </div>

            {/* Points per scan */}
            <Card className="border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Pikë për Skanim</h3>
                    <p className="text-sm text-slate-500">Sa pikë fiton përdoruesi për çdo skanim valid</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min="1"
                    value={pointsPerScan}
                    onChange={(e) => setPointsPerScan(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-32 border-slate-300 text-lg font-bold text-center"
                    data-testid="points-per-scan-input"
                  />
                  <span className="text-slate-500">pikë</span>
                </div>
              </CardContent>
            </Card>

            {/* Reward levels */}
            <Card className="border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                    <Star className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Nivelet e Shpërblimeve</h3>
                    <p className="text-sm text-slate-500">Vendos sa pikë nevojiten për çdo nivel</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {rewardLevels.map((level) => (
                    <div 
                      key={level.level}
                      className="p-4 rounded-xl border border-slate-200 bg-slate-50 space-y-3"
                    >
                      {/* Level header row */}
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-white shadow flex items-center justify-center text-xl flex-shrink-0">
                          {level.level === 1 && '🌱'}
                          {level.level === 2 && '⭐'}
                          {level.level === 3 && '🔍'}
                          {level.level === 4 && '🦸'}
                          {level.level === 5 && '👑'}
                        </div>
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                          {/* Level name */}
                          <div>
                            <Label className="text-xs text-slate-500">Emri i Nivelit {level.level}</Label>
                            <Input
                              value={level.name}
                              onChange={(e) => updateLevelName(level.level, e.target.value)}
                              className="border-slate-300 font-semibold"
                              data-testid={`level-${level.level}-name-input`}
                            />
                          </div>
                          {/* Points */}
                          <div>
                            <Label className="text-xs text-slate-500">Pikë të nevojshme</Label>
                            {level.level === 1 ? (
                              <div className="h-10 flex items-center text-sm text-slate-400 px-3">0 (fillon gjithmonë nga 0)</div>
                            ) : (
                              <Input
                                type="number"
                                min="1"
                                value={level.min_points}
                                onChange={(e) => updateLevelPoints(level.level, e.target.value)}
                                className="border-slate-300 font-bold"
                                data-testid={`level-${level.level}-points-input`}
                              />
                            )}
                          </div>
                          {/* Has reward toggle */}
                          <div>
                            <Label className="text-xs text-slate-500">Ka shpërblim?</Label>
                            <select
                              value={level.reward ? 'yes' : 'no'}
                              onChange={(e) => toggleLevelReward(level.level, e.target.value === 'yes')}
                              className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                              data-testid={`level-${level.level}-has-reward`}
                            >
                              <option value="no">Jo</option>
                              <option value="yes">Po</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Reward details (if has reward) */}
                      {level.reward && (
                        <div className="ml-14 grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-slate-200">
                          <div>
                            <Label className="text-xs text-slate-500">Lloji i shpërblimit</Label>
                            <select
                              value={level.reward.type}
                              onChange={(e) => updateLevelReward(level.level, 'type', e.target.value)}
                              className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                              data-testid={`level-${level.level}-reward-type`}
                            >
                              <option value="sticker">Stiker</option>
                              <option value="keychain">Key Chain</option>
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Sasia</Label>
                            <Input
                              type="number"
                              min="1"
                              value={level.reward.quantity}
                              onChange={(e) => updateLevelReward(level.level, 'quantity', e.target.value)}
                              className="border-slate-300"
                              data-testid={`level-${level.level}-reward-qty`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Mesazhi i shpërblimit</Label>
                            <Input
                              value={level.reward.message}
                              onChange={(e) => updateLevelReward(level.level, 'message', e.target.value)}
                              className="border-slate-300"
                              placeholder="p.sh. Urime! Fitove 1 stiker falas!"
                              data-testid={`level-${level.level}-reward-msg`}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex justify-end">
                  <Button
                    onClick={saveRewardConfig}
                    disabled={savingRewards}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="save-rewards-btn"
                  >
                    {savingRewards ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Ruaj Ndryshimet
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
